import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import { API_BASE_URL } from "./config";
import "./suporte.css";

const MAX_PRINTS = 3;

const statusLabelMap = {
  aberto: "Aberto",
  em_andamento: "Em andamento",
  resolvido: "Resolvido",
  fechado: "Fechado",
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("pt-BR");
};

const SuporteLayout = () => {
  const [titulo, setTitulo] = useState("");
  const [erro, setErro] = useState("");
  const [pagina, setPagina] = useState("");
  const [passos, setPassos] = useState("");
  const [prints, setPrints] = useState([]);
  const [expandedTicketId, setExpandedTicketId] = useState(null);
  const [messagesByTicketId, setMessagesByTicketId] = useState({});
  const [isLoadingMessagesForTicket, setIsLoadingMessagesForTicket] = useState(null);
  const [messageText, setMessageText] = useState("");
  const [messagePrints, setMessagePrints] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingTickets, setIsLoadingTickets] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [attachmentBlobUrls, setAttachmentBlobUrls] = useState({});
  const [isIvanDeveloperView, setIsIvanDeveloperView] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const attachmentBlobUrlsRef = useRef({});

  const userName = useMemo(() => localStorage.getItem("userName") || "Usuario", []);
  const userId = useMemo(() => Number(localStorage.getItem("userId")) || null, []);
  const printPreviews = useMemo(
    () =>
      prints.map((file) => ({
        name: file.name,
        size: file.size,
        url: URL.createObjectURL(file),
      })),
    [prints]
  );
  const messagePrintPreviews = useMemo(
    () =>
      messagePrints.map((file) => ({
        name: file.name,
        size: file.size,
        url: URL.createObjectURL(file),
      })),
    [messagePrints]
  );

  useEffect(() => {
    return () => {
      printPreviews.forEach((preview) => URL.revokeObjectURL(preview.url));
    };
  }, [printPreviews]);
  useEffect(() => {
    return () => {
      messagePrintPreviews.forEach((preview) => URL.revokeObjectURL(preview.url));
    };
  }, [messagePrintPreviews]);

  const clearForm = () => {
    setTitulo("");
    setErro("");
    setPagina("");
    setPassos("");
    setPrints([]);
  };

  const fetchMyTickets = async () => {
    setIsLoadingTickets(true);
    try {
      setErrorMessage("");
      const response = await axios.get(`${API_BASE_URL}/suporte/tickets`);
      const payload = response.data;

      if (Array.isArray(payload)) {
        setTickets(payload);
        setIsIvanDeveloperView(false);
      } else {
        setTickets(Array.isArray(payload?.tickets) ? payload.tickets : []);
        setIsIvanDeveloperView(Boolean(payload?.isIvanDeveloper));
      }
    } catch (error) {
      console.error("Erro ao buscar tickets de suporte:", error);
      setErrorMessage("Nao foi possivel carregar seus tickets agora.");
    } finally {
      setIsLoadingTickets(false);
    }
  };

  useEffect(() => {
    fetchMyTickets();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const revokeBlobUrls = (map) => {
      Object.values(map).forEach((blobUrl) => {
        if (blobUrl) URL.revokeObjectURL(blobUrl);
      });
    };

    const loadAttachmentBlobs = async () => {
      revokeBlobUrls(attachmentBlobUrlsRef.current);
      attachmentBlobUrlsRef.current = {};
      setAttachmentBlobUrls({});

      const allAnexos = tickets.flatMap((ticket) => (Array.isArray(ticket.anexos) ? ticket.anexos : []));
      if (!allAnexos.length) return;

      const nextBlobMap = {};

      await Promise.all(
        allAnexos.map(async (anexo) => {
          try {
            const response = await axios.get(anexo.url, { responseType: "blob" });
            const blobUrl = URL.createObjectURL(response.data);
            nextBlobMap[anexo.id] = blobUrl;
          } catch (error) {
            console.error("Erro ao carregar anexo protegido:", error);
          }
        })
      );

      if (cancelled) {
        revokeBlobUrls(nextBlobMap);
        return;
      }

      attachmentBlobUrlsRef.current = nextBlobMap;
      setAttachmentBlobUrls(nextBlobMap);
    };

    loadAttachmentBlobs();

    return () => {
      cancelled = true;
    };
  }, [tickets]);

  useEffect(() => {
    return () => {
      Object.values(attachmentBlobUrlsRef.current).forEach((blobUrl) => {
        if (blobUrl) URL.revokeObjectURL(blobUrl);
      });
    };
  }, []);

  const handleFilesChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length > MAX_PRINTS) {
      setErrorMessage(`Voce pode anexar no maximo ${MAX_PRINTS} prints.`);
      setPrints(selectedFiles.slice(0, MAX_PRINTS));
      return;
    }
    setErrorMessage("");
    setPrints(selectedFiles);
  };

  const removePrint = (index) => {
    setPrints((prev) => prev.filter((_, i) => i !== index));
  };

  const handleMessageFilesChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length > MAX_PRINTS) {
      setErrorMessage(`Voce pode anexar no maximo ${MAX_PRINTS} prints.`);
      setMessagePrints(selectedFiles.slice(0, MAX_PRINTS));
      return;
    }
    setErrorMessage("");
    setMessagePrints(selectedFiles);
  };

  const removeMessagePrint = (index) => {
    setMessagePrints((prev) => prev.filter((_, i) => i !== index));
  };

  const loadSecureAttachmentBlobs = async (anexos = []) => {
    const pending = (Array.isArray(anexos) ? anexos : []).filter((anexo) => anexo?.id && !attachmentBlobUrlsRef.current[anexo.id]);
    if (!pending.length) return;

    const nextBlobMap = {};
    await Promise.all(
      pending.map(async (anexo) => {
        try {
          const response = await axios.get(anexo.url, { responseType: "blob" });
          nextBlobMap[anexo.id] = URL.createObjectURL(response.data);
        } catch (error) {
          console.error("Erro ao carregar anexo protegido:", error);
        }
      })
    );

    attachmentBlobUrlsRef.current = { ...attachmentBlobUrlsRef.current, ...nextBlobMap };
    setAttachmentBlobUrls((prev) => ({ ...prev, ...nextBlobMap }));
  };

  const fetchTicketMessages = async (ticketId) => {
    setIsLoadingMessagesForTicket(ticketId);
    try {
      setErrorMessage("");
      const response = await axios.get(`${API_BASE_URL}/suporte/tickets/${ticketId}/messages`);
      const payload = response.data;
      const mensagens = Array.isArray(payload?.mensagens) ? payload.mensagens : [];
      setMessagesByTicketId((prev) => ({ ...prev, [ticketId]: mensagens }));

      const anexos = mensagens.flatMap((msg) => (Array.isArray(msg.anexos) ? msg.anexos : []));
      await loadSecureAttachmentBlobs(anexos);
    } catch (error) {
      console.error("Erro ao buscar mensagens do ticket:", error);
      setErrorMessage("Nao foi possivel carregar a conversa deste ticket.");
    } finally {
      setIsLoadingMessagesForTicket(null);
    }
  };

  const toggleTicketConversation = async (ticketId) => {
    if (expandedTicketId === ticketId) {
      setExpandedTicketId(null);
      setMessageText("");
      setMessagePrints([]);
      return;
    }
    setErrorMessage("");
    setExpandedTicketId(ticketId);
    setMessageText("");
    setMessagePrints([]);
    await fetchTicketMessages(ticketId);
  };

  const handleSendMessage = async (ticketId) => {
    setSuccessMessage("");
    setErrorMessage("");

    if (!messageText.trim()) {
      setErrorMessage("Digite uma mensagem para enviar.");
      return;
    }

    if (messagePrints.length > MAX_PRINTS) {
      setErrorMessage(`Voce pode anexar no maximo ${MAX_PRINTS} prints.`);
      return;
    }

    const formData = new FormData();
    formData.append("mensagem", messageText.trim());
    messagePrints.forEach((file) => formData.append("prints", file));

    setIsSubmitting(true);
    try {
      await axios.post(`${API_BASE_URL}/suporte/tickets/${ticketId}/messages`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      setMessageText("");
      setMessagePrints([]);
      await fetchTicketMessages(ticketId);
      await fetchMyTickets();
    } catch (error) {
      console.error("Erro ao enviar mensagem:", error);
      const backendMessage =
        error?.response?.data?.msg ||
        error?.response?.data?.error ||
        "Nao foi possivel enviar a mensagem agora.";
      setErrorMessage(backendMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSuccessMessage("");
    setErrorMessage("");

    if (!titulo.trim() || !erro.trim() || !pagina.trim() || !passos.trim()) {
      setErrorMessage("Preencha todos os campos obrigatorios.");
      return;
    }

    if (prints.length > MAX_PRINTS) {
      setErrorMessage(`Voce pode anexar no maximo ${MAX_PRINTS} prints.`);
      return;
    }

    const formData = new FormData();
    formData.append("titulo", titulo.trim());
    formData.append("erro", erro.trim());
    formData.append("pagina", pagina.trim());
    formData.append("passos", passos.trim());
    prints.forEach((file) => formData.append("prints", file));

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/suporte/tickets`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const ticketCode = response?.data?.ticket?.codigo || "sem codigo";
      setSuccessMessage(`Ticket ${ticketCode} enviado com sucesso para o gestor.`);
      clearForm();
      await fetchMyTickets();
    } catch (error) {
      console.error("Erro ao enviar ticket de suporte:", error);
      const backendMessage =
        error?.response?.data?.msg ||
        error?.response?.data?.error ||
        "Nao foi possivel enviar o ticket agora.";
      setErrorMessage(backendMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Row className="gy-4">
        <Col xs={12}>
          <Card className="suporte-card">
            <Card.Body>
              <div className="suporte-header">
                <h5 className="mb-1">Abrir ticket de suporte</h5>
                <span className="text-secondary-light text-sm">
                  Usuario: <strong>{userName}</strong>
                </span>
              </div>

              {successMessage ? <Alert variant="success">{successMessage}</Alert> : null}
              {errorMessage ? <Alert variant="danger">{errorMessage}</Alert> : null}

              <Form onSubmit={handleSubmit}>
                <Row className="gy-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Titulo do problema *</Form.Label>
                      <Form.Control
                        type="text"
                        value={titulo}
                        onChange={(e) => setTitulo(e.target.value)}
                        placeholder="Ex: Erro ao salvar matricula"
                        maxLength={255}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Pagina onde acontece *</Form.Label>
                      <Form.Control
                        type="text"
                        value={pagina}
                        onChange={(e) => setPagina(e.target.value)}
                        placeholder="Ex: /cadastro-matricula"
                        maxLength={255}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label>Mensagem de erro *</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={erro}
                        onChange={(e) => setErro(e.target.value)}
                        placeholder="Cole a mensagem do erro ou descreva o que aparece na tela"
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label>Passo a passo para reproduzir *</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        value={passos}
                        onChange={(e) => setPassos(e.target.value)}
                        placeholder="1. Entrei na tela X ... 2. Cliquei em Y ..."
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label>Anexar prints (maximo 3)</Form.Label>
                      <div className="suporte-upload-box">
                        <Form.Control
                          type="file"
                          accept="image/png, image/jpeg, image/jpg, image/webp"
                          multiple
                          onChange={handleFilesChange}
                        />
                        <div className="suporte-upload-meta">
                          <span>{prints.length} anexado(s)</span>
                          <span>{MAX_PRINTS - prints.length} vaga(s)</span>
                        </div>
                      </div>
                      <div className="form-text">Aceita PNG, JPG e WEBP (ate 8MB cada).</div>
                    </Form.Group>
                  </Col>
                </Row>

                {prints.length > 0 ? (
                  <div className="suporte-preview-grid mt-16">
                    {printPreviews.map((preview, index) => (
                      <div key={`${preview.name}-${index}`} className="suporte-preview-item">
                        <img src={preview.url} alt={preview.name} className="suporte-preview-image" />
                        <button
                          type="button"
                          className="suporte-preview-remove"
                          onClick={() => removePrint(index)}
                          aria-label={`Remover ${preview.name}`}
                        >
                          x
                        </button>
                        <div className="suporte-preview-info">
                          <span className="suporte-file-name">{preview.name}</span>
                          <span className="suporte-file-size">
                            {(preview.size / 1024 / 1024).toFixed(2)} MB
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}

                <div className="d-flex gap-2 mt-24">
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Enviando...
                      </>
                    ) : (
                      "Enviar ticket"
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline-secondary"
                    onClick={clearForm}
                    disabled={isSubmitting}
                  >
                    Limpar
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12}>
          <Card className="suporte-card">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-16">
                <h6 className="mb-0">
                  {isIvanDeveloperView ? "Todos os tickets (Visao Dev)" : "Meus tickets"}
                </h6>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={fetchMyTickets}
                  disabled={isLoadingTickets}
                >
                  Atualizar
                </Button>
              </div>

              {isLoadingTickets ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                </div>
              ) : tickets.length === 0 ? (
                <p className="text-secondary-light mb-0">
                  {isIvanDeveloperView
                    ? "Nenhum ticket encontrado no sistema."
                    : "Nenhum ticket aberto ainda."}
                </p>
              ) : (
                <div className="suporte-ticket-list">
                  {tickets.map((ticket) => (
                    <div key={ticket.cp_sup_id} className="suporte-ticket-item">
                      <div className="suporte-ticket-top">
                        <strong>{ticket.cp_sup_codigo || `#${ticket.cp_sup_id}`}</strong>
                        <Badge bg="primary">
                          {statusLabelMap[ticket.cp_sup_status] || ticket.cp_sup_status || "Aberto"}
                        </Badge>
                      </div>
                      <div className="text-sm mt-1">
                        <strong>Titulo:</strong> {ticket.cp_sup_titulo}
                      </div>
                      {isIvanDeveloperView ? (
                        <div className="text-sm">
                          <strong>Solicitante:</strong> {ticket.cp_sup_usuario_nome}
                        </div>
                      ) : null}
                      <div className="text-sm">
                        <strong>Pagina:</strong> {ticket.cp_sup_pagina}
                      </div>
                      <div className="text-sm">
                        <strong>Erro:</strong> {ticket.cp_sup_erro}
                      </div>
                      <div className="text-sm">
                        <strong>Passo a passo:</strong> {ticket.cp_sup_passos}
                      </div>
                      <div className="text-sm">
                        <strong>Destino:</strong> {ticket.cp_sup_destino_nome}
                      </div>
                      <div className="text-sm">
                        <strong>Criado em:</strong> {formatDateTime(ticket.cp_sup_created_at)}
                      </div>

                      {Array.isArray(ticket.anexos) && ticket.anexos.length > 0 ? (
                        <div className="mt-2">
                          <strong className="text-sm">Anexos:</strong>
                          <div className="suporte-anexo-gallery">
                            {ticket.anexos.map((anexo) => {
                              const secureUrl = attachmentBlobUrls[anexo.id];
                              if (!secureUrl) return null;
                              return (
                                <a
                                  key={anexo.id}
                                  href={secureUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="suporte-anexo-thumb"
                                  title={anexo.nomeOriginal}
                                >
                                  <img src={secureUrl} alt={anexo.nomeOriginal} className="suporte-anexo-image" />
                                  <span className="suporte-anexo-caption">{anexo.nomeOriginal}</span>
                                </a>
                              );
                            })}
                          </div>
                        </div>
                      ) : null}

                      <div className="d-flex justify-content-end mt-12">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => toggleTicketConversation(ticket.cp_sup_id)}
                          disabled={isLoadingMessagesForTicket === ticket.cp_sup_id}
                        >
                          {expandedTicketId === ticket.cp_sup_id ? "Fechar conversa" : "Ver conversa"}
                        </Button>
                      </div>

                      {expandedTicketId === ticket.cp_sup_id ? (
                        <div className="suporte-conversa mt-12">
                          {isLoadingMessagesForTicket === ticket.cp_sup_id ? (
                            <div className="text-center py-3">
                              <Spinner animation="border" size="sm" />
                            </div>
                          ) : (
                            <div className="suporte-chat">
                              {(messagesByTicketId[ticket.cp_sup_id] || []).length === 0 ? (
                                <div className="text-secondary-light text-sm">Nenhuma mensagem neste ticket ainda.</div>
                              ) : (
                                (messagesByTicketId[ticket.cp_sup_id] || []).map((msg) => {
                                  const authorType = msg.cp_sup_autor_tipo || "usuario";
                                  const isSupport = authorType === "suporte";
                                  return (
                                    <div
                                      key={msg.cp_sup_msg_id}
                                      className={`suporte-msg ${isSupport ? "suporte-msg-suporte" : "suporte-msg-user"}`}
                                    >
                                      <div className="suporte-msg-meta">
                                        <strong>{msg.cp_sup_autor_nome}</strong>
                                        <span className="text-secondary-light">{formatDateTime(msg.cp_sup_created_at)}</span>
                                      </div>
                                      <div className="suporte-msg-text">{msg.cp_sup_mensagem}</div>
                                      {Array.isArray(msg.anexos) && msg.anexos.length > 0 ? (
                                        <div className="suporte-anexo-gallery mt-8">
                                          {msg.anexos.map((anexo) => {
                                            const secureUrl = attachmentBlobUrls[anexo.id];
                                            if (!secureUrl) return null;
                                            return (
                                              <a
                                                key={anexo.id}
                                                href={secureUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="suporte-anexo-thumb"
                                                title={anexo.nomeOriginal}
                                              >
                                                <img
                                                  src={secureUrl}
                                                  alt={anexo.nomeOriginal}
                                                  className="suporte-anexo-image"
                                                />
                                                <span className="suporte-anexo-caption">{anexo.nomeOriginal}</span>
                                              </a>
                                            );
                                          })}
                                        </div>
                                      ) : null}
                                    </div>
                                  );
                                })
                              )}
                            </div>
                          )}

                          {(() => {
                            const canSend =
                              isIvanDeveloperView || (userId && Number(ticket.cp_sup_usuario_id) === Number(userId));
                            if (!canSend) return null;

                            return (
                              <div className="mt-12">
                                <Form
                                  onSubmit={(e) => {
                                    e.preventDefault();
                                    handleSendMessage(ticket.cp_sup_id);
                                  }}
                                >
                                  <Form.Group className="mb-2">
                                    <Form.Label className="mb-1">
                                      {isIvanDeveloperView ? "Resposta do suporte" : "Enviar mensagem"}
                                    </Form.Label>
                                    <Form.Control
                                      as="textarea"
                                      rows={3}
                                      value={messageText}
                                      onChange={(e) => setMessageText(e.target.value)}
                                      placeholder={isIvanDeveloperView ? "Digite sua resposta..." : "Digite uma atualizacao..."}
                                      disabled={isSubmitting}
                                    />
                                  </Form.Group>

                                  <Form.Group className="mb-2">
                                    <Form.Label className="mb-1">Anexar prints (maximo 3)</Form.Label>
                                    <Form.Control
                                      type="file"
                                      accept="image/png, image/jpeg, image/jpg, image/webp"
                                      multiple
                                      onChange={handleMessageFilesChange}
                                      disabled={isSubmitting}
                                    />
                                    <div className="form-text">Aceita PNG, JPG e WEBP (ate 8MB cada).</div>
                                  </Form.Group>

                                  {messagePrints.length > 0 ? (
                                    <div className="suporte-preview-grid mt-12">
                                      {messagePrintPreviews.map((preview, index) => (
                                        <div key={`${preview.name}-${index}`} className="suporte-preview-item">
                                          <img src={preview.url} alt={preview.name} className="suporte-preview-image" />
                                          <button
                                            type="button"
                                            className="suporte-preview-remove"
                                            onClick={() => removeMessagePrint(index)}
                                            aria-label={`Remover ${preview.name}`}
                                            disabled={isSubmitting}
                                          >
                                            x
                                          </button>
                                          <div className="suporte-preview-info">
                                            <span className="suporte-file-name">{preview.name}</span>
                                            <span className="suporte-file-size">
                                              {(preview.size / 1024 / 1024).toFixed(2)} MB
                                            </span>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : null}

                                  <div className="d-flex justify-content-end gap-2 mt-12">
                                    <Button type="submit" disabled={isSubmitting}>
                                      {isSubmitting ? (
                                        <>
                                          <Spinner animation="border" size="sm" className="me-2" />
                                          Enviando...
                                        </>
                                      ) : (
                                        "Enviar"
                                      )}
                                    </Button>
                                  </div>
                                </Form>
                              </div>
                            );
                          })()}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default SuporteLayout;
